import { GetSalesPortalOrderGuideItemsQuery } from '@/types';
import { groupByItem } from '@/experiences/sales-portal/domain/order-guides/blocs/transformers/group-by-item';
import { OrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { toOrderGuideItem } from '@/experiences/sales-portal/domain/order-guides/blocs/transformers/order-guide-item-transformer';

type CollectionResponse =
  GetSalesPortalOrderGuideItemsQuery['order_guide_restaurant_collection'][number];

type SupplierCollectionResponse =
  GetSalesPortalOrderGuideItemsQuery['order_guide_supplier_collection'][number];

const compareBySortOrder = (
  a: number | null | undefined,
  b: number | null | undefined,
): number => {
  const aSortOrder = a ?? Number.POSITIVE_INFINITY;
  const bSortOrder = b ?? Number.POSITIVE_INFINITY;
  if (aSortOrder === bSortOrder) {
    return 0;
  }
  return aSortOrder - bSortOrder;
};

export const transformOrderGuideItems = (
  data: GetSalesPortalOrderGuideItemsQuery,
  collection: CollectionResponse,
  isDistributorLocked: boolean,
): OrderGuide => {
  const restaurantPartitionUUIDs = new Set(
    collection.order_guide_restaurant_partitions.map(
      (partition) => partition.uuid,
    ),
  );

  // supplier/distributor-locked guides render their default group only; editable guides
  // surface every partition
  const sourcePartitions = isDistributorLocked
    ? collection.order_guide_restaurant_partitions.filter(
        (partition) => partition.is_default,
      )
    : collection.order_guide_restaurant_partitions;

  const partitions = sourcePartitions.map((partition) => {
    const items = data.getSalesPortalOrderGuideItems
      .filter((item) => {
        // Backend returns supplier level og items when there is a merge strategy enabled
        // so merge them into our default partition as storefront does
        if (
          !item.order_guide_item_partition_id ||
          !restaurantPartitionUUIDs.has(item.order_guide_item_partition_id)
        ) {
          return partition.is_default;
        }
        return (
          !!item.entry_info &&
          item.order_guide_item_partition_id === partition.uuid
        );
      })
      .sort((a, b) =>
        compareBySortOrder(a.entry_info?.sort_order, b.entry_info?.sort_order),
      )
      .map(toOrderGuideItem);

    return {
      isDefault: partition.is_default,
      itemGroups: groupByItem(items, (item) => item.itemUUID),
      name: partition.name ?? null,
      partitionUUID: partition.uuid,
    };
  });

  return {
    collectionUUID: collection.uuid,
    isDefault: collection.is_default,
    name: collection.name ?? null,
    partitions,
  };
};

export const transformSupplierOrderGuideItems = (
  data: GetSalesPortalOrderGuideItemsQuery,
  collection: SupplierCollectionResponse,
): OrderGuide => {
  const supplierPartitionUUIDs = new Set(
    collection.order_guide_supplier_partitions.map(
      (partition) => partition.uuid,
    ),
  );

  const partitions = collection.order_guide_supplier_partitions.map(
    (partition) => {
      const items = data.getSalesPortalOrderGuideItems
        .filter((item) => {
          if (
            !item.order_guide_item_partition_id ||
            !supplierPartitionUUIDs.has(item.order_guide_item_partition_id)
          ) {
            return partition.is_default;
          }
          return item.order_guide_item_partition_id === partition.uuid;
        })
        .sort((a, b) =>
          compareBySortOrder(
            a.entry_info?.sort_order,
            b.entry_info?.sort_order,
          ),
        )
        .map(toOrderGuideItem);

      return {
        isDefault: partition.is_default,
        itemGroups: groupByItem(items, (item) => item.itemUUID),
        name: partition.name ?? null,
        partitionUUID: partition.uuid,
      };
    },
  );

  return {
    collectionUUID: collection.uuid,
    isDefault: collection.is_default,
    name: collection.name ?? null,
    partitions,
  };
};
